import { Box, TextField, MenuItem, Typography, Tooltip } from '@mui/material';
import { colors } from '../../styles/theme.js';

/**
 * Watchdog · Eingabefelder des Expertenmodus (FR-1).
 *
 * Die Felder speichern NICHT selbst. Sie melden Änderungen nach oben, die
 * Detailansicht sammelt sie im Entwurf und schreibt sie gebündelt über den
 * Speichern-Button. So bleibt nachvollziehbar, was noch nicht in der Datenbank
 * steht — und ein versehentlicher Klick daneben ändert nichts.
 *
 * Geänderte Felder werden am gelben Rand und am Punkt links erkennbar.
 */

interface FieldProps {
  /** Aktueller Wert im Entwurf. */
  value: string | null;
  /** Wert wie er in der Datenbank steht — für die Änderungsmarkierung. */
  original: string | null;
  readOnly?: boolean;
  placeholder?: string;
  error?: string | null;
  onChange: (value: string) => void;
}

const norm = (v: string | null | undefined) => (v ?? '').trim();

function DirtyDot({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <Tooltip title="Noch nicht gespeichert">
      <Box component="span" sx={{
        width: 7, height: 7, borderRadius: '50%', bgcolor: colors.scoreYellow,
        display: 'inline-block', mr: 0.7, flexShrink: 0,
      }} />
    </Tooltip>
  );
}

function FieldError({ message }: { message?: string | null }) {
  if (!message) return null;
  return (
    <Typography variant="caption" sx={{ color: colors.scoreRed, display: 'block', mt: 0.3 }}>
      {message}
    </Typography>
  );
}

function dirtySx(dirty: boolean) {
  return dirty
    ? { '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.scoreYellow, borderWidth: 1.5 } }
    : undefined;
}

export function EditableText({ value, original, readOnly, placeholder, error, onChange, mono, multiline }:
  FieldProps & { mono?: boolean; multiline?: boolean }) {
  const dirty = norm(value) !== norm(original);

  if (readOnly) {
    return value
      ? <Typography variant="body2" sx={mono ? { fontFamily: "'JetBrains Mono', monospace" } : undefined}>{value}</Typography>
      : <Typography variant="caption" sx={{ color: colors.black60 }}>—</Typography>;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
      <Box sx={{ pt: 1.3 }}><DirtyDot show={dirty} /></Box>
      <Box sx={{ flex: 1 }}>
        <TextField
          size="small" variant="outlined" fullWidth
          value={value ?? ''}
          placeholder={placeholder}
          multiline={multiline}
          error={Boolean(error)}
          onChange={(e) => onChange(e.target.value)}
          sx={dirtySx(dirty)}
          InputProps={{ sx: mono ? { fontFamily: "'JetBrains Mono', monospace", fontSize: 13 } : { fontSize: 13.5 } }}
        />
        <FieldError message={error} />
      </Box>
    </Box>
  );
}

export function EditableSelect({ value, original, readOnly, options, error, onChange }:
  FieldProps & { options: readonly { value: string; label: string }[] }) {
  const dirty = norm(value) !== norm(original);

  if (readOnly) {
    const label = options.find((o) => o.value === value)?.label ?? value ?? '—';
    return <Typography variant="body2">{label}</Typography>;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
      <Box sx={{ pt: 1.3 }}><DirtyDot show={dirty} /></Box>
      <Box sx={{ flex: 1 }}>
        <TextField
          select size="small" variant="outlined" fullWidth
          value={value ?? ''}
          error={Boolean(error)}
          onChange={(e) => onChange(e.target.value)}
          sx={dirtySx(dirty)}
          InputProps={{ sx: { fontSize: 13.5 } }}
        >
          {options.map((o) => (
            <MenuItem key={o.value} value={o.value} sx={{ fontSize: 13.5 }}>{o.label}</MenuItem>
          ))}
        </TextField>
        <FieldError message={error} />
      </Box>
    </Box>
  );
}

/**
 * Fokus-Schalter. Der Stern ist additiv (E6, 17.09.2026): er setzt oder
 * entfernt den Fokus dieses Events, die übrigen Fokus-Events bleiben — wie
 * die Mehrfachauswahl im Kunden-Wizard.
 */
export function FocusToggle({ active, original, readOnly, onChange }: {
  active: boolean;
  original: boolean;
  readOnly?: boolean;
  onChange: (next: boolean) => void;
}) {
  const dirty = active !== original;
  const star = (
    <Box component="span" sx={{ fontSize: 16, lineHeight: 1, color: active ? colors.lime : colors.silver }}>
      {active ? '★' : '☆'}
    </Box>
  );

  if (readOnly) return active ? <Tooltip title="Fokus-Event"><Box component="span">{star}</Box></Tooltip> : <></>;

  return (
    <Tooltip title={dirty ? 'Geändert — noch nicht gespeichert' : active ? 'Fokus entfernen' : 'Als Fokus-Event setzen'}>
      <Box
        component="span"
        onClick={() => onChange(!active)}
        sx={{
          cursor: 'pointer', userSelect: 'none',
          ...(dirty ? { outline: `1.5px solid ${colors.scoreYellow}`, borderRadius: '3px', px: 0.2 } : {}),
        }}
      >
        {star}
      </Box>
    </Tooltip>
  );
}
